import { useCallback, useRef, useState, memo, forwardRef, useImperativeHandle, useEffect, useMemo } from "react";
import ReactFlow, { addEdge, Background, useNodesState, useEdgesState, MiniMap, Controls, MarkerType } from "reactflow";
import { useNavigate } from "react-router-dom";
import "reactflow/dist/style.css";
import "./overview.css";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";

import { GroupWidget, Node, NodeWidget } from "./Node";
import Workspace from "../../../../service/workspace";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { showToast } from "../../../../store/actions";
import {
  closeConfirm,
  closeConflictConfirm,
  closeStopConfirm,
  handleEditJob,
  handleJobRun,
  handleJobStop,
} from "../../../../store/actions/datapipeline";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import { ConfirmPopup } from "../../../Common";
import { isEqual, setStorageValue } from "../../../../util/common";
import { navScheduledJobs } from "../../../kmk/AppData";
import { DATA_SOURCE } from "../../../../util/Data/DataModeling";
import DataPipelineService from "../../../../service/dataPipelineService";

const getId = () => `dndnode_${Math.random().toString()}`;

const findParentNode = (nodes, node) => {
  const nodeX = node.position.x;
  const nodeY = node.position.y;
  const parentNodes = nodes.filter((element) => element.type === "groupWidget"); // Assuming you have a specific type for parent nodes

  for (const parent of parentNodes) {
    if (parent.id === node.id) continue;
    const parentX = parent.position.x;
    const parentY = parent.position.y;
    const parentWidth = parent.width;
    const parentHeight = parent.height;

    if (
      nodeX >= parentX &&
      nodeY >= parentY &&
      nodeX + node.width <= parentX + parentWidth &&
      nodeY + node.height <= parentY + parentHeight
    ) {
      return parent;
    }
  }

  return null;
};

const nodeTypes = {
  jobNode: Node,
  widget: NodeWidget,
  groupWidget: GroupWidget,
};

const proOptions = { hideAttribution: true };

const nameMap = new Map(
  Object.entries({
    1: "Data Load",
    4: "Data Quality",
    5: "Data Mastering",
    6: "Data Deduplication",
    2: "Data Exchange",
    3: "Data Transform",
    7: "Notebook",
    8: "Routing",
    9: "Notification",
    groupWidget: "Group",
    widget: "Some Note",
  })
);
const getNodeName = (type) => nameMap.get(type);

//Building parent/child for edges
const buildGraph = (edges) => {
  const graph = {};

  for (const edge of edges) {
    const { source, target } = edge;
    if (!graph[source]) {
      graph[source] = [];
    }
    graph[source].push(target);
  }

  return graph;
};

//Checking if edes have node cycle
const hasCycle = (graph, node, visited, recursionStack) => {
  if (!visited[node]) {
    visited[node] = true;
    recursionStack[node] = true;

    if (graph[node]) {
      for (const child of graph[node]) {
        if ((!visited[child] && hasCycle(graph, child, visited, recursionStack)) || recursionStack[child]) return true;
      }
    }
  }

  recursionStack[node] = false;
  return false;
};

//Creating graph of edges and checking for cycle of nodes
const hasInfiniteLoop = (edges) => {
  const graph = buildGraph(edges);

  const visited = {};
  const recursionStack = {};

  for (const node in graph) {
    if (!visited[node] && hasCycle(graph, node, visited, recursionStack)) return true;
  }

  return false;
};

const DATA_MODELING_JOBS = [1, 4, 5, 6];

const ConflictPopup = ({ data, JobType, loading, handleJobRun, handleCancel }) => (
  <ConfirmPopup
    configData={{
      popupTitle: (
        <Box sx={{ m: 0, p: 0, display: "flex", alignItems: "center" }}>
          <ReportProblemIcon color="warning" sx={{ mr: "8px" }} />
          <Typography>Attention</Typography>
        </Box>
      ),
      popupMessage: (
        <>
          <Typography variant="body2">
            {JobType === 1 &&
              `One or more running jobs have same ${DATA_SOURCE.plural.toLowerCase()} selected and/or mapped entities.`}
            {JobType === 4 && `One or more running jobs have same ${DATA_SOURCE.plural.toLowerCase()} selected.`}
            {[5, 6].includes(JobType) &&
              `One or more running jobs have same data entities selected and/or has a mapped ${DATA_SOURCE.singular.toLowerCase()} in another job.`}
          </Typography>
          {data.jobs?.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                Conflicting Jobs
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.jobs.map((job, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={job}>
                      <ListItemText primary={`${job}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          {data.dataSources.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                {`Conflicting ${DATA_SOURCE.plural}`}
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.dataSources.map((ds, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={ds}>
                      <ListItemText primary={`${ds}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          {data.entities.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                Conflicting Entities
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.entities.map((entity, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={entity}>
                      <ListItemText primary={`${entity}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          <Typography variant="body2" mt={2}>
            Running this job concurrently may lead to inconsistent output or potential data loss.
          </Typography>
        </>
      ),
      handleCancel,
      handleClick: () => handleJobRun(false),
      confirmBtnName: "Run Anyway",
      loadingForConfirm: loading,
    }}
  />
);

const getEdgeParams = (jobType, sourceHandle) => {
  if (sourceHandle === "bottom")
    return {
      style: {
        stroke: "red",
      },
    };

  if (jobType === 8 && sourceHandle === "right_bottom")
    return {
      label: "No",
    };

  if (jobType === 8)
    return {
      label: "Yes",
    };

  return {};
};

const getMarkerParams = (sourceHandle) => {
  if (sourceHandle === "bottom")
    return {
      color: "red",
    };

  return {};
};

const Canvas = (
  {
    defaultViewport,
    initialNodes,
    initialEdges,
    pipelineName,
    editPanelRef,
    prevNodes,
    prevEdges,
    entryPoint,
    isDefault,
    pipelineRef,
    successEnable = false,
    successEmails = [],
    errorEnable = false,
    errorEmails = [],
  },
  ref
) => {
  const { Workspace_id, DataPipeline_id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const [loading, setLoading] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const user = useSelector((state) => state.user);
  const { User_id } = useMemo(() => user?.currentUser, [user?.currentUser]);

  const { editJob, confirmPopupOpen, stopConfirmPopupOpen, conflictConfirmPopupOpen } = useMemo(
    () => dataPipeline,
    [dataPipeline]
  );

  useEffect(() => {
    if (!editJob) return;

    const node = nodes.find((_node) => _node.id === editJob.id);
    if (!node || node.data?.status === editJob.data?.status) return;

    setNodes((prev) =>
      prev.map((_node) =>
        _node.id === node.id
          ? {
              ..._node,
              data: {
                ..._node.data,
                status: editJob.data?.status,
              },
            }
          : _node
      )
    );
  }, [editJob, nodes, setNodes]);

  const onConnect = useCallback(
    (params) => {
      //Checking it new edge will create infinite loop
      if (hasInfiniteLoop([params, ...edges]))
        return dispatch(
          showToast({
            message: "Circular pipelining is not allowed.",
            type: "error",
          })
        );

      const currentNodeType = nodes.find(({ id }) => id === params.source).data.type;

      //Checking if connecting to same job in routing jobs
      if (
        currentNodeType === 8 &&
        params.sourceHandle !== "bottom" &&
        edges.find(
          ({ source, target, sourceHandle }) =>
            source === params.source &&
            target === params.target &&
            ((sourceHandle === "right" && params.sourceHandle === "right_bottom") ||
              (sourceHandle === "right_bottom" && params.sourceHandle === "right"))
        )
      )
        return dispatch(
          showToast({
            message: "Success and Alternative job outcomes cannot be the same.",
            type: "error",
          })
        );

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            ...getEdgeParams(currentNodeType, params.sourceHandle),
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 30,
              height: 30,
              ...getMarkerParams(params.sourceHandle),
            },
          },
          eds
        )
      );
    },
    [dispatch, edges, nodes, setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      // check if the dropped element is valid
      if (typeof type === "undefined" || !type) return;

      const sameTypeNodes = nodes
        .filter((_node) => _node.data?.type && _node.data.type.toString() === type.toString())
        .map(({ data }) => data.label);
      const nodeName = getNodeName(type);

      let name = "";

      //Generating name for Nodes like Group_1, Group_2 etc
      let counter = sameTypeNodes.length + 1;
      name = nodeName + "_" + counter;

      while (sameTypeNodes.includes(name)) name = nodeName + "_" + ++counter;

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const options = {};

      if (type === "groupWidget") {
        options.type = type;
      } else if (type === "widget") {
        options.type = "widget";
      } else options.type = "jobNode";

      const newNode = {
        id: getId(),
        ...options,
        position,
        data: { label: name, type: ["groupWidget", "widget"].includes(type) ? type : parseInt(type) },
      };

      setNodes((nds) => nds.concat(newNode));

      if (options.type === "jobNode") dispatch(handleEditJob(newNode));
    },
    [dispatch, nodes, reactFlowInstance, setNodes]
  );

  const onNodeDragStop = useCallback(
    (event, node) => {
      event.preventDefault();
      if (node.parentNode || node.type === "groupWidget") return;

      const parent = findParentNode(nodes, node); // Find the potential parent node

      if (parent && node?.parentNode !== parent.id) {
        setNodes((prev) =>
          prev.map((_node) =>
            _node.id === node.id
              ? {
                  ..._node,
                  parentNode: parent.id,
                  extent: "parent",
                  position: {
                    x: _node.position.x - parent.position.x,
                    y: _node.position.y - parent.position.y,
                  },
                }
              : _node
          )
        );
      }
    },
    [nodes, setNodes]
  );

  const addNewDataPipeline = useCallback(
    async (savingWhileAddingJob = false) => {
      setLoading(true);
      const pipeline = new DataPipelineService();
      const response = await pipeline.addNewDataPipeline(Workspace_id, {
        name: pipelineName,
        nodes: savingWhileAddingJob ? nodes.filter(({ type }) => type !== "jobNode") : nodes,
        edges: savingWhileAddingJob ? [] : edges,
        viewport: reactFlowInstance.getViewport(),
        entryPoint: "",
        isDefault,
        successEnable: successEnable || false,
        successEmails: successEmails || [],
        errorEnable: errorEnable || false,
        errorEmails: errorEmails || [],
      });

      if (response.success) {
        setStorageValue(`${User_id}_PipelineFilters`, {}, (storedValue) => {
          let selectedPipelines = storedValue[Workspace_id]?.["selectedPipelines"] || [];
          if (selectedPipelines?.length) selectedPipelines?.push(response.data.DataPipeline_id);
          const updatedFilters = {
            ...storedValue,
            [Workspace_id]: { ...storedValue[Workspace_id], selectedPipelines },
          };

          return updatedFilters;
        });

        const nodes = response.data.UpdatedIDs;

        setNodes((prev) => {
          const updatedNodes = prev.map((_node) => {
            if (!_node.id.startsWith("dndnode_")) return _node;

            const newId = nodes.find(({ oldID }) => oldID === _node.id)?.newID;
            if (!newId) return _node;

            return {
              ..._node,
              id: newId + "",
            };
          });

          prevNodes.current = updatedNodes.map(({ id, position, data }) => ({ id, position, label: data.label }));
          return [...updatedNodes];
        });

        setEdges((prev) => {
          const updatedEdges = prev.map((_edge) => {
            if (!_edge.source.startsWith("dndnode_") && !_edge.target.startsWith("dndnode_")) return _edge;

            if (_edge.source.startsWith("dndnode_")) {
              const newId = nodes.find(({ oldID }) => oldID === _edge.source)?.newID;
              if (!newId) return _edge;

              _edge.source = newId + "";
            }

            if (_edge.target.startsWith("dndnode_")) {
              const newId = nodes.find(({ oldID }) => oldID === _edge.target)?.newID;
              if (!newId) return _edge;

              _edge.target = newId + "";
            }

            return {
              ..._edge,
            };
          });

          prevEdges.current = updatedEdges.map(({ id, source, target }) => ({ id, source, target }));
          return [...updatedEdges];
        });

        //Updating default values as new changes saved to db
        pipelineRef.current = {
          pipelineName,
          isDefault: !!isDefault,
          entryPoint: "",
          successEnable: successEnable || false,
          successEmails: successEmails || [],
          errorEnable: errorEnable || false,
          errorEmails: errorEmails || [],
        };

        setLoading(false);

        if (!savingWhileAddingJob) {
          dispatch(showToast({ message: response.message, type: "success" }));
          navigate(
            `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/data-pipeline/${response.data.DataPipeline_id}`
          );
        }
      } else if (response?.error?.validation) {
        let errorMessage = [];
        for (let index of Object.keys(response.error.validation)) {
          errorMessage.push(...response.error.validation[index]);
        }
        dispatch(
          showToast({
            message: errorMessage,
            type: "error",
          })
        );
      }
      setLoading(false);
      return response;
    },
    [
      User_id,
      Workspace_id,
      dispatch,
      edges,
      isDefault,
      navigate,
      nodes,
      pipelineName,
      pipelineRef,
      prevEdges,
      prevNodes,
      reactFlowInstance,
      setEdges,
      setNodes,
      successEnable,
      successEmails,
      errorEnable,
      errorEmails,
    ]
  );

  const removeEditingNode = useCallback(
    (id) => {
      setNodes((prev) => prev.filter((node) => node.id !== id));
    },
    [setNodes]
  );

  const saveDataPipeline = useCallback(
    async (clone = false) => {
      setLoading(true);

      let _entryPoint = null;
      if (entryPoint && nodes.some(({ id }) => parseInt(id) === parseInt(entryPoint))) _entryPoint = entryPoint;

      const pipeline = new DataPipelineService();
      const response = await pipeline.saveDataPipeline(Workspace_id, DataPipeline_id, {
        name: pipelineName,
        nodes,
        edges,
        viewport: reactFlowInstance.getViewport(),
        entryPoint: _entryPoint,
        isDefault,
        successEnable: successEnable || false,
        successEmails: successEmails || [],
        errorEnable: errorEnable || false,
        errorEmails: errorEmails || [],
      });

      if (response.success) {
        if (nodes.length) {
          const nodes = response.data.UpdatedIDs;
          //Updating node ids
          setNodes((prev) => {
            const updatedNodes = prev.map((_node) => {
              if (!_node.id.startsWith("dndnode_")) return _node;

              const newId = nodes.find(({ oldID }) => oldID === _node.id)?.newID;
              if (!newId) return _node;

              return {
                ..._node,
                id: newId + "",
              };
            });

            prevNodes.current = [
              ...updatedNodes.map(({ id, position, data }) => ({ id, position, label: data.label })),
            ];
            return [...updatedNodes];
          });

          //Updating source and target ids
          setEdges((prev) => {
            const updatedEdges = prev.map((_edge) => {
              if (!_edge.source.startsWith("dndnode_") && !_edge.target.startsWith("dndnode_")) return _edge;

              if (_edge.source.startsWith("dndnode_")) {
                const newId = nodes.find(({ oldID }) => oldID === _edge.source)?.newID;
                if (!newId) return _edge;

                _edge.source = newId + "";
              }

              if (_edge.target.startsWith("dndnode_")) {
                const newId = nodes.find(({ oldID }) => oldID === _edge.target)?.newID;
                if (!newId) return _edge;

                _edge.target = newId + "";
              }

              return {
                ..._edge,
              };
            });

            prevEdges.current = [...updatedEdges.map(({ id, source, target }) => ({ id, source, target }))];
            return [...updatedEdges];
          });
        } else {
          prevNodes.current = [...nodes.map(({ id, position, data }) => ({ id, position, label: data.label }))];
          prevEdges.current = [...edges.map(({ id, source, target }) => ({ id, source, target }))];
        }

        //Updating default values as new changes saved to db
        pipelineRef.current = {
          pipelineName,
          isDefault: !!isDefault,
          entryPoint: entryPoint || "",
          successEnable: successEnable || false,
          successEmails: successEmails || [],
          errorEnable: errorEnable || false,
          errorEmails: errorEmails || [],
        };

        if (!clone) dispatch(showToast({ message: response.message, type: "success" }));
      } else if (response?.error?.validation) {
        let errorMessage = [];
        for (let index of Object.keys(response.error.validation)) {
          errorMessage.push(...response.error.validation[index]);
        }

        dispatch(showToast({ message: errorMessage, type: "error" }));
      }

      setLoading(false);
      return response.success;
    },
    [
      Workspace_id,
      DataPipeline_id,
      pipelineName,
      nodes,
      edges,
      reactFlowInstance,
      entryPoint,
      isDefault,
      dispatch,
      setNodes,
      setEdges,
      prevNodes,
      prevEdges,
      pipelineRef,
      successEnable,
      successEmails,
      errorEnable,
      errorEmails,
    ]
  );

  // Handling canvas and previous state as per the job saved
  const updateCanvas = useCallback(
    (action, data) => {
      if (!DataPipeline_id) return;
      const { prevId = false, JobPipelineId, Job_id, name: label, position } = data;
      if (action === "Add") {
        if (DataPipeline_id.toString() !== JobPipelineId.toString())
          setNodes((prev) => prev.filter((node) => node.id !== prevId));
        else {
          prevNodes.current = [
            ...prevNodes.current,
            {
              id: data.Job_id + "",
              label,
              position,
            },
          ];
          setNodes((prev) =>
            prev.map((node) =>
              node.id === prevId
                ? {
                    ...node,
                    id: data.Job_id + "",
                    data: {
                      ...node.data,
                      label,
                    },
                  }
                : node
            )
          );
        }
      } else if (action === "Update") {
        if (DataPipeline_id.toString() !== JobPipelineId.toString()) {
          setEdges((prev) =>
            prev.filter(
              (edge) => edge.source.toString() !== Job_id.toString() && edge.target.toString() !== Job_id.toString()
            )
          );

          // Handling prev comparison for blocker
          prevEdges.current = prevEdges.current.filter(
            (edge) => edge.source.toString() !== Job_id.toString() && edge.target.toString() !== Job_id.toString()
          );

          setNodes((prev) => prev.filter((node) => node.id.toString() !== Job_id.toString()));
          prevNodes.current = prevNodes.current.filter((node) => node.id.toString() !== Job_id.toString()); // Handling prev comparison for blocker
        } else
          setNodes((prev) =>
            prev.map((node) =>
              node.id.toString() === Job_id.toString()
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      label,
                    },
                  }
                : node
            )
          );

        // Handling prev comparison for blocker
        prevNodes.current = prevNodes.current.map((node) =>
          node.id.toString() === Job_id.toString()
            ? {
                ...node,
                label,
              }
            : node
        );
      }
    },
    [DataPipeline_id, prevEdges, prevNodes, setEdges, setNodes]
  );

  // If there are nay change in node or edges which needs to be tracked
  const restrictOrNot = useCallback(() => {
    const currentNodes = nodes.map(({ id, position, data }) => ({ id, position, label: data.label }));
    const currentEdges = edges.map(({ id, source, target }) => ({ id, source, target }));

    const pipelineChanged = !isEqual(pipelineRef.current, {
      pipelineName,
      isDefault: !!isDefault,
      entryPoint: entryPoint || "",
      successEnable: successEnable || false,
      successEmails: successEmails || [],
      errorEnable: errorEnable || false,
      errorEmails: errorEmails || [],
    });

    if (pipelineChanged) return true;

    const canvasChanged = !isEqual(
      { nodes: currentNodes, edges: currentEdges },
      { nodes: prevNodes.current, edges: prevEdges.current }
    );

    return canvasChanged;
  }, [
    edges,
    entryPoint,
    isDefault,
    nodes,
    pipelineName,
    pipelineRef,
    prevEdges,
    prevNodes,
    successEnable,
    successEmails,
    errorEnable,
    errorEmails,
  ]);

  useImperativeHandle(ref, () => ({
    nodes,
    saveDataPipeline,
    addNewDataPipeline,
    removeEditingNode,
    updateCanvas,
    restrictOrNot,
  }));

  useEffect(() => {
    if (DataPipeline_id) return;
    if (nodes.length)
      setNodes((prev) =>
        prev.map((node) => {
          node.data.newPipelineName = pipelineName;
          return node;
        })
      );

    return () => {};
  }, [DataPipeline_id, nodes.length, pipelineName, setNodes]);

  //Setting default viewport
  useEffect(() => {
    if (defaultViewport && reactFlowInstance) reactFlowInstance.setViewport(defaultViewport);
  }, [defaultViewport, reactFlowInstance]);

  //Fetching results every 20 seconds for running jobs
  useEffect(() => {
    const fetchData = async () => {
      const runningJobs = nodes
        .filter(({ data }) => data.status === "running" || data.status === "terminating")
        .map((_node) => _node.id);
      if (!runningJobs.length) return;

      const wsService = new Workspace();
      const { data, success } = await wsService.getAllJobs(Workspace_id, {
        search: "",
        jobTypeList: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        jobIds: [runningJobs],
        pipelineList: [DataPipeline_id],
      });

      if (success) {
        setNodes((prev) => {
          //Creating job_id => index map
          const jobIndexMap = {};
          prev.forEach((item, index) => (jobIndexMap[item.id] = index));

          const runningJobsIndex = [];
          const _nodes = prev.map((node) => {
            if (["running", "terminating"].includes(node.data?.status)) {
              const job = data.jobs.find((_job) => _job.Job_id.toString() === node.id);

              if (job && job.status !== node.data?.status) {
                if (editJob) {
                  dispatch(
                    handleEditJob({
                      ...editJob,
                      data: { ...editJob.data, status: job.status },
                    })
                  );
                }

                if (job.ErrorJob_id && job.ErrorPipeline_id === parseInt(DataPipeline_id) && job.status !== "success")
                  runningJobsIndex.push(job.ErrorJob_id);
                if (job.Type === 8 && job.status === "success") {
                  if (
                    job.NextJob_id &&
                    job.NextPipeline_id === parseInt(DataPipeline_id) &&
                    job.Detail.result?.isResultTrue
                  )
                    runningJobsIndex.push(job.NextJob_id);
                  if (
                    job.Detail.alternateJob &&
                    parseInt(job.Detail?.alternatePipeline) === parseInt(DataPipeline_id) &&
                    !job.Detail.result?.isResultTrue
                  )
                    runningJobsIndex.push(job.Detail.alternateJob);
                } else if (
                  job.NextJob_id &&
                  job.NextPipeline_id === parseInt(DataPipeline_id) &&
                  job.status === "success"
                )
                  runningJobsIndex.push(job.NextJob_id);

                return {
                  ...node,
                  data: { ...node.data, status: job.status },
                };
              }

              return node;
            }
            return node;
          });

          //updating next job status to running
          runningJobsIndex.forEach((id) => {
            _nodes[jobIndexMap[id]] = {
              ..._nodes[jobIndexMap[id]],
              data: { ..._nodes[jobIndexMap[id]].data, status: "running" },
            };
          });

          return _nodes;
        });
      }
    };

    const interval = setInterval(fetchData, 20000); // Update every 20 seconds

    return () => clearInterval(interval);
  }, [Workspace_id, dispatch, editJob, nodes, setNodes]);

  const updateNodes = useCallback(
    (node) => {
      prevNodes.current = [
        ...prevNodes.current,
        {
          id: node.id,
          label: node.data.label,
          position: node.position,
        },
      ];
      setNodes((prev) => [...prev, node]);
    },
    [prevNodes, setNodes]
  );

  const $nodes = useMemo(() => {
    return nodes.map((node) => {
      node.data.editPanelRef = editPanelRef;
      node.data.savePipeline = saveDataPipeline;
      node.data.updateNodes = updateNodes;
      return node;
    });
  }, [nodes, editPanelRef, saveDataPipeline, updateNodes]);

  //Handle run job
  const handleRun = useCallback(
    async (Job_id, JobType, validate = true) => {
      if (DATA_MODELING_JOBS.includes(JobType)) setLoading(true);

      const response = await dispatch(handleJobRun(Workspace_id, Job_id, JobType, validate));

      if (response?.success) {
        const { Job_id, status } = response.data;

        setNodes((prev) => {
          return prev.map((node) => (node.id === Job_id ? { ...node, data: { ...node.data, status } } : node));
        });

        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );
      }

      if (DATA_MODELING_JOBS.includes(JobType)) setLoading(false);
    },
    [Workspace_id, dispatch, setNodes]
  );

  //Handle stop job
  const handleStopJobExecution = useCallback(
    async (Job_id, JobType) => {
      setLoading(true);

      const response = await dispatch(handleJobStop(Workspace_id, Job_id, JobType));
      if (response?.data) {
        const { Job_id, status } = response.data;

        setNodes((prev) => {
          return prev.map((node) => (node.id === Job_id ? { ...node, data: { ...node.data, status } } : node));
        });

        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );
      }

      setLoading(false);
    },
    [Workspace_id, dispatch, setNodes]
  );

  return (
    <>
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: 99999 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <div className="reactflow-wrapper" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={$nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeDragStop={onNodeDragStop}
          nodeTypes={nodeTypes}
          proOptions={proOptions}
          fitView
          deleteKeyCode={"None"}
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>

      {confirmPopupOpen.open && (
        <ConfirmPopup
          configData={{
            popupTitle:
              confirmPopupOpen.JobType === 1
                ? "Load Data to Data Warehouse"
                : confirmPopupOpen.JobType === 4
                ? "Run Data Quality Job"
                : confirmPopupOpen.JobType === 5
                ? "Run Data Mastering Job"
                : "Run Data Deduplication Job",
            popupMessage: "Are you sure you want to continue?",
            handleCancel: () => dispatch(closeConfirm()),
            handleClick: () => handleRun(confirmPopupOpen.Job_id, confirmPopupOpen.JobType, true),
          }}
        />
      )}
      {conflictConfirmPopupOpen.open && (
        <ConflictPopup
          handleJobRun={() => handleRun(conflictConfirmPopupOpen.Job_id, conflictConfirmPopupOpen.JobType, false)}
          loading={loading}
          handleCancel={() => dispatch(closeConflictConfirm())}
          data={conflictConfirmPopupOpen.data}
          JobType={conflictConfirmPopupOpen.JobType}
        />
      )}
      {stopConfirmPopupOpen.open && (
        <ConfirmPopup
          configData={{
            popupTitle: `Terminate "${stopConfirmPopupOpen.JobName || ""}" Job`,
            popupMessage: "Are you sure you want to terminate this job?",
            handleCancel: () => dispatch(closeStopConfirm()),
            handleClick: () => handleStopJobExecution(stopConfirmPopupOpen.Job_id, stopConfirmPopupOpen.JobType),
          }}
        />
      )}
    </>
  );
};

export default memo(forwardRef(Canvas));
